import { useMemo } from 'react'
import type { CustomerOrder } from '../../customerOrdersApi'
import type { ShopLanguage, ShopLocale, ShopMessageKey } from '../../../i18n'
import { createSupportRequestPathBuilder } from '../../helpers/supportRequestPath'
import { resolveStatusFromQuery } from './ordersListShared'
import { useSkeletonVisibility } from './useSkeletonVisibility'
import {
  useOrdersListFlowEffects,
  useOrdersListFlowReducer,
  useOrdersListFlowSubmitHandlers,
  useOrdersListFlowViewModel,
} from './orders-list-flow'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type UseCustomerOrdersListControllerInput = {
  orders: CustomerOrder[]
  ordersLoading: boolean
  language: ShopLanguage
  locale: ShopLocale
  t: TranslateFn
  toLocalizedPath: (path: string) => string
  onNavigate: (targetPath: string) => void
}

/**
 * Orders-list flow orchestrator: state machine + effects + submit handlers + view-model.
 */
export const useCustomerOrdersListController = ({
  orders,
  ordersLoading,
  language,
  locale,
  t,
  toLocalizedPath,
  onNavigate,
}: UseCustomerOrdersListControllerInput) => {
  const initialStatusFilters = useMemo(() => resolveStatusFromQuery(), [])
  const [state, dispatch] = useOrdersListFlowReducer({ initialStatusFilters })

  const buildSupportRequestPath = useMemo(
    () => createSupportRequestPathBuilder(toLocalizedPath),
    [toLocalizedPath],
  )

  const handlers = useOrdersListFlowSubmitHandlers({
    dispatch,
    filtersOpen: state.filtersOpen,
    onNavigate,
    toLocalizedPath,
    buildSupportRequestPath,
  })

  const viewModel = useOrdersListFlowViewModel({
    state,
    orders,
    language,
    locale,
    t,
    onResetDateFilter: handlers.onResetDateFilter,
    onRemoveStatusFilter: handlers.onRemoveStatusFilter,
    onResetPriceRange: handlers.onResetPriceRange,
  })

  useOrdersListFlowEffects({
    state,
    totalPages: viewModel.totalPages,
    dispatch,
  })

  const isLoadingSkeletonVisible = useSkeletonVisibility({
    isLoading: ordersLoading,
    minVisibleMs: 920,
  })

  return {
    searchValue: state.searchValue,
    filtersOpen: state.filtersOpen,
    sortBy: state.sortBy,
    dateFilter: state.dateFilter,
    customDateFrom: state.customDateFrom,
    customDateTo: state.customDateTo,
    statusFilters: state.statusFilters,
    priceMin: state.priceMin,
    priceMax: state.priceMax,
    pageSize: state.pageSize,
    currentPage: state.currentPage,
    dateFormatter: viewModel.dateFormatter,
    sortOptions: viewModel.sortOptions,
    dateFilterOptions: viewModel.dateFilterOptions,
    pageSizeOptions: viewModel.pageSizeOptions,
    filteredOrders: viewModel.filteredOrders,
    totalPages: viewModel.totalPages,
    visiblePages: viewModel.visiblePages,
    pagination: viewModel.pagination,
    paginatedOrders: viewModel.paginatedOrders,
    activeFilterChips: viewModel.activeFilterChips,
    isLoadingSkeletonVisible,
    onSearchChange: handlers.onSearchChange,
    onToggleFilters: handlers.onToggleFilters,
    onSortChange: handlers.onSortChange,
    onDateFilterChange: handlers.onDateFilterChange,
    onCustomDateFromChange: handlers.onCustomDateFromChange,
    onCustomDateToChange: handlers.onCustomDateToChange,
    onToggleStatusFilter: handlers.onToggleStatusFilter,
    onPriceMinChange: handlers.onPriceMinChange,
    onPriceMaxChange: handlers.onPriceMaxChange,
    onClearAllFilters: handlers.onClearAllFilters,
    onSetPageSize: handlers.onSetPageSize,
    onSetCurrentPage: handlers.onSetCurrentPage,
    onOpenOrderDetail: handlers.onOpenOrderDetail,
    onBuyAgain: handlers.onBuyAgain,
    onRequestSupport: handlers.onRequestSupport,
    onOpenCatalog: handlers.onOpenCatalog,
  }
}
